/* eslint-disable no-prototype-builtins */
import { createSelector } from "reselect";
import { feedEntitiesWithGeoByTypeSelector } from "orion-components/GlobalData/Selectors";
import { buildHierarchy } from "./utils";
import cloneDeep from "lodash/cloneDeep";
import { getDeviceStateHexColor } from "../DeviceState/utils";

const getCompositeDeviceStateColorPriority = (devices) => {
	if (!devices || devices.length === 0) return null;
	const highPriorityDevice = devices.reduce((min, device) => {
		return device.deviceStatePriority < min.deviceStatePriority ? device : min;
	});
	return highPriorityDevice.deviceStateColor;
};

// Function to roll up and remove duplicate command-label pairs
export const rollupUniqueCommands = (arrays) => {
	const allCommands = arrays.flat(); // Flatten the arrays into one

	// Reduce to accumulate unique commands with aggregated entityIds
	const uniqueCommands = allCommands.reduce((acc, current) => {
		const existingCommand = acc.find((cmd) => cmd.command === current.command && cmd.label === current.label);

		if (existingCommand) {
			// If the command already exists, append the entityIds and flatten them
			existingCommand.entityIds = Array.from(
				new Set([
					...existingCommand.entityIds,
					...(Array.isArray(current.entityIds) ? current.entityIds : [current.entityIds])
				])
			);
		} else {
			// Otherwise, add the command with entityIds as a flat array
			acc.push({
				...current,
				entityIds: Array.isArray(current.entityIds) ? current.entityIds : [current.entityIds]
			});
		}

		return acc;
	}, []);

	return uniqueCommands;
};

const deviceGroupsBaseSelector = (state) => cloneDeep(state?.globalData?.deviceGroups) || {};

const devicesSelector = (state) => {
	const devices = feedEntitiesWithGeoByTypeSelector("device")(state);
	return devices ? devices : {};
};

const camerasSelector = (state) => {
	const cameras = feedEntitiesWithGeoByTypeSelector("camera")(state);
	return cameras ? cameras : {};
};
const deviceTypeSelector = (state) => state?.globalData?.deviceTypes ?? {};

const deviceStateSelector = (state) => state?.globalData?.deviceState;

const compositeDeviceSelector = (state) => state?.globalData?.compositeDevices ?? {};

export const getAllDevicesSelector = createSelector(
	[
		devicesSelector,
		camerasSelector,
		deviceTypeSelector,
		deviceStateSelector,
		compositeDeviceSelector,
		(_, props) => props // Include props explicitly
	],
	(devices = [], cameras = [], deviceTypes = {}, deviceState = {}, compositeDevice = {}, props) => {
		const { deviceStateColor, ignoreDevices, ignoreCameras, ignoreCompositeDevices, entitiesToExclude = [], allowedEntities } = props || {}; // Destructure deviceStateColor from props
		const allDevices = cloneDeep({
			...(!ignoreDevices ? devices : {}),
			...(!ignoreCameras ? cameras : {}),
			...(!ignoreCompositeDevices ? compositeDevice : {})
		});

		for (const id in allDevices) {
			if (allDevices.hasOwnProperty(id)) {
				const device = allDevices[id];

				// Remove excluded entities. To remove any specific entities, the id should be passed in the entitiesToExclude array.
				// Allow only specfic entities. If allowedEntities is passed, only those entities will be included and the rest will be removed.
				if (entitiesToExclude?.includes(id) || (allowedEntities && !allowedEntities?.includes(id))) delete allDevices[id];

				if (device?.entityType === "compositeDevice") {
					const children = [];
					const deviceCommands = [];
					device.isCompositeDevice = true;
					const devicesAndCameras = cloneDeep({ ...devices, ...cameras });

					for (const uuid in devicesAndCameras) {
						const data = devicesAndCameras[uuid];

						if (device?.id === data?.compositeDeviceId) {
							const deviceType = data?.deviceType;
							data.isDevice = true;
							data.isGroup = false;
							data.name = data?.entityData?.properties?.name;
							data.deviceType = deviceTypes[deviceType]?.name || null;
							data.icon = deviceTypes[deviceType]?.icon || null;
							data.iconGroup = deviceTypes[deviceType]?.iconGroup || null;
							data.deviceStateColor = getDeviceStateHexColor(
								deviceState?.[data?.entityData?.properties?.deviceState]?.color
							);
							data.deviceStatePriority =
								deviceState?.[data?.entityData?.properties?.deviceState]?.priority;

							// Filter children by deviceStateColor
							if (deviceStateColor?.length === 0 || deviceStateColor?.includes(data.deviceStateColor)) {
								children.push(data);
							}

							const commands = data?.entityData?.properties?.commands;
							if (commands) {
								const filteredCommands = commands
									.flat()
									.filter((item) => typeof item === "object" && !Array.isArray(item))
									.map((command) => ({
										...command,
										entityIds: [data?.id]
									}));

								deviceCommands.push(...filteredCommands);
							}
						}
					}

					device.children = children;
					device.deviceCommands = rollupUniqueCommands(deviceCommands);
					device.deviceStateColor = getCompositeDeviceStateColorPriority(device.children);
				} else {
					device.name = device?.entityData?.properties?.name;
					device.deviceStateColor = getDeviceStateHexColor(
						deviceState?.[device?.entityData?.properties?.deviceState]?.color
					);

					// Correctly filter top-level devices
					if (deviceStateColor?.length > 0 && !deviceStateColor.includes(device.deviceStateColor)) {
						delete allDevices[id]; // Remove non-matching devices
					}
				}

				const deviceType = device?.deviceType;
				device.deviceType = deviceTypes[deviceType]?.name || null;
				device.icon = deviceTypes[deviceType]?.icon || null;
				device.iconGroup = deviceTypes[deviceType]?.iconGroup || null;
			}
		}

		return allDevices;
	}
);

export const getAllDeviceGroupsSelector = createSelector(
	[
		deviceGroupsBaseSelector,
		(state, props) => getAllDevicesSelector(state, props),
		(_, props) => props?.searchString,
		(_, props) => props?.ignoreDevices,
		(_, props) => props?.ignoreEmptyGroups,
		(_, props) => props?.addUngroupedDevicesToRoot
	],
	(deviceGroups, allDevices, searchString, ignoreDevices, ignoreEmptyGroups, addUngroupedDevicesToRoot) => {
		// Add level to the hierarchy and include parentGroupId and parentLevel
		const addLevelsAndParentGroupId = (node, currentLevel = 0, parentGroupId = null, parentLevel = null) => {
			// Assign the current level, parentGroupId, and parentLevel to the node
			const nodeWithLevel = {
				...node,
				level: currentLevel,
				parentGroupId,
				parentLevel
			};

			// Recursively assign levels and parentGroupId to children
			if (node?.children?.length > 0) {
				nodeWithLevel.children = node.children.map((child) =>
					addLevelsAndParentGroupId(
						child,
						currentLevel + (child?.children?.length > 0 ? 1 : 2),
						node.id,
						currentLevel
					)
				);
			}

			return nodeWithLevel;
		};

		// Build hierarchy and add levels
		const hierarchyWithLevels = addLevelsAndParentGroupId(
			buildHierarchy(deviceGroups, ignoreDevices ? {} : allDevices, ignoreEmptyGroups, addUngroupedDevicesToRoot)
		);

		// Return all data if searchString is empty or undefined
		if (!searchString || searchString.trim() === "") {
			return [hierarchyWithLevels];
		}

		// Recursive filter function
		const filterHierarchy = (node) => {
			const match = node?.name?.toLowerCase().includes(searchString.toLowerCase());

			// Recursively filter children
			const filteredChildren = node?.children?.map(filterHierarchy).filter(Boolean) || [];

			// Include node if it matches the search string or has matching children
			return match || filteredChildren.length > 0 ? { ...node, children: filteredChildren } : null;
		};

		// Apply filtering to the root node's children
		const filteredData = filterHierarchy(hierarchyWithLevels);

		return filteredData ? [filteredData] : [];
	}
);

export const getDeviceFilterCounts = createSelector(
	[devicesSelector, camerasSelector, compositeDeviceSelector, deviceStateSelector],
	(devices, cameras, compositeDevices, deviceState) => {
		const allDevices = cloneDeep({ ...devices, ...cameras, ...compositeDevices });

		// Initialize an empty object for dynamic color counts
		const colorCounts = {};

		// Iterate over all device objects dynamically
		Object.values(allDevices).forEach((device) => {
			const color = deviceState?.[device?.entityData?.properties?.deviceState]?.color;

			if (color) {
				// Increment the count for the color or initialize it to 1
				colorCounts[color] = (colorCounts[color] || 0) + 1;
			}
		});

		return colorCounts; // Return the dynamic color counts
	}
);
